'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

interface SignupFormData {
  nombre: string;
  email: string;
  telefono: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
}

interface SignupFormProps {
  onSwitchToLogin: () => void;
}

export default function SignupForm({ onSwitchToLogin }: SignupFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const router = useRouter();
  const { signUp } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<SignupFormData>();

  const passwordValue = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true);
    try {
      await signUp(data.email, data.password, {
        fullName: data.nombre,
        avatarUrl: '',
      });
      toast.success('Cuenta creada exitosamente', {
        description: `Bienvenido, ${data.nombre}. Tu espacio está listo.`,
      });
      router.push('/dashboard-principal');
    } catch (err: any) {
      setIsLoading(false);
      const msg = err?.message || '';
      if (msg.includes('already registered') || msg.includes('already been registered') || msg.includes('User already registered')) {
        setError('email', { message: 'Este correo ya está registrado' });
      } else if (msg.includes('Password should be')) {
        setError('password', { message: 'La contraseña debe tener al menos 6 caracteres' });
      } else {
        setError('email', { message: 'No se pudo crear la cuenta. Intenta nuevamente.' });
      }
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-700 text-foreground">Crear cuenta</h2>
        <p className="text-sm text-muted-foreground mt-1">Comienza a controlar tus negocios hoy</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label htmlFor="signup-nombre" className="block text-sm font-600 text-foreground mb-1.5">
            Nombre completo
          </label>
          <input
            id="signup-nombre"
            type="text"
            placeholder="Tu nombre"
            {...register('nombre', { required: 'El nombre es requerido', minLength: { value: 2, message: 'Mínimo 2 caracteres' } })}
            className={`w-full px-3 py-2.5 bg-input border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors ${errors.nombre ? 'border-danger' : 'border-border'}`}
          />
          {errors.nombre && (
            <p className="mt-1 text-xs text-danger flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={12} />
              {errors.nombre.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="signup-email" className="block text-sm font-600 text-foreground mb-1.5">
            Correo electrónico
          </label>
          <input
            id="signup-email"
            type="email"
            placeholder="tu@correo.com"
            {...register('email', {
              required: 'El correo es requerido',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Correo inválido' },
            })}
            className={`w-full px-3 py-2.5 bg-input border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors ${errors.email ? 'border-danger' : 'border-border'}`}
          />
          {errors.email && (
            <p className="mt-1 text-xs text-danger flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={12} />
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="signup-telefono" className="block text-sm font-600 text-foreground mb-1.5">
            Teléfono <span className="text-xs font-400 text-muted-foreground">(opcional)</span>
          </label>
          <input
            id="signup-telefono"
            type="tel"
            placeholder="+504 9999-9999"
            {...register('telefono')}
            className="w-full px-3 py-2.5 bg-input border border-border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors"
          />
        </div>

        <div>
          <label htmlFor="signup-password" className="block text-sm font-600 text-foreground mb-1.5">
            Contraseña
          </label>
          <div className="relative">
            <input
              id="signup-password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Mínimo 8 caracteres"
              {...register('password', {
                required: 'La contraseña es requerida',
                minLength: { value: 8, message: 'Mínimo 8 caracteres' },
              })}
              className={`w-full px-3 py-2.5 pr-10 bg-input border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors ${errors.password ? 'border-danger' : 'border-border'}`}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
              <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.password && (
            <p className="mt-1 text-xs text-danger flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={12} />
              {errors.password.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor="signup-confirm" className="block text-sm font-600 text-foreground mb-1.5">
            Confirmar contraseña
          </label>
          <div className="relative">
            <input
              id="signup-confirm"
              type={showConfirm ? 'text' : 'password'}
              placeholder="••••••••"
              {...register('confirmPassword', {
                required: 'Confirma tu contraseña',
                validate: (v) => v === passwordValue || 'Las contraseñas no coinciden',
              })}
              className={`w-full px-3 py-2.5 pr-10 bg-input border rounded-md text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-colors ${errors.confirmPassword ? 'border-danger' : 'border-border'}`}
            />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
              <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1 text-xs text-danger flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={12} />
              {errors.confirmPassword.message}
            </p>
          )}
        </div>

        <div>
          <div className="flex items-start gap-2">
            <input
              id="signup-terms"
              type="checkbox"
              {...register('terms', { required: 'Debes aceptar los términos' })}
              className="w-4 h-4 mt-0.5 rounded border-border bg-input accent-primary flex-shrink-0"
            />
            <label htmlFor="signup-terms" className="text-sm text-muted-foreground leading-snug">
              Acepto los Términos de Servicio y la Política de Privacidad
            </label>
          </div>
          {errors.terms && (
            <p className="mt-1 text-xs text-danger flex items-center gap-1">
              <Icon name="ExclamationCircleIcon" size={12} />
              {errors.terms.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-2.5 bg-primary text-primary-foreground font-600 text-sm rounded-md hover:bg-primary/90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              Creando cuenta...
            </>
          ) : (
            'Crear Cuenta Gratis'
          )}
        </button>

        <p className="text-center text-sm text-muted-foreground">
          ¿Ya tienes cuenta?{' '}
          <button type="button" onClick={onSwitchToLogin} className="text-primary hover:text-primary/80 font-600 transition-colors">
            Iniciar sesión
          </button>
        </p>
      </form>
    </div>
  );
}